import styled, { css } from 'styled-components';
import arrowDown from '~/assets/icons/arrow_down_2.svg';
import delZoneBg from '../../assets/images/del_zone_bg.svg';
import bg from '../../assets/images/man_stroke.png';
import line from '../../assets/images/quick_line.svg';
import { TextLabel } from '../TextLabel';

const GRID_STYLES = {
	columns: {
		character: 'repeat(3, 1fr)',
		quickAccess: 'repeat(4, 1fr)',
		inventory: 'repeat(5, 1fr)',
		backpack: 'repeat(5, 1fr)',
		trunk: 'repeat(4, 1fr)',
	},
};

const StyledContainer = styled.div<{ $banner?: boolean }>`
	position: relative;
	height: 100%;
	background-image: url(${bg});
	background-repeat: no-repeat;
	background-size: 500px 780px;
	background-position: calc(50% - 580px)
		${(props) => (props.$banner ? 120 : 60)}px;
`;

const StyledBannerContainer = styled.div`
	padding: 20px 40px 0;
	min-height: 70px;
`;

const StyledInventoryContainer = styled.div`
	display: grid;
	grid-template-columns: 350px 440px 350px;
	justify-content: center;
	gap: 180px;
	padding-top: 90px;

	@media (max-width: 1500px) {
		justify-content: space-between;
		gap: 0;
		padding: 90px 40px;
	}
`;

const StyledFlexContainer = styled.div<{
	$direction?: 'row' | 'column';
	$gap?: number;
	$align?: string;
	$justify?: string;
}>`
	position: relative;
	display: flex;
	flex-direction: ${(props) => props.$direction || 'column'};
	gap: ${(props) => props.$gap || '0'}px;
	align-items: ${(props) => props.$align || 'stretch'};
	justify-content: ${(props) => props.$justify || 'start'};
`;

const StyledGridContainer = styled.div<{
	$columns?: string;
	$gap?: number;
	$rowgap?: number;
	$colgap?: number;
	$rows?: string;
}>`
	display: grid;
	grid-template-columns: ${(props) => props.$columns};
	gap: ${(props) => props.$gap || '10'}px;
	row-gap: ${(props) => props.$rowgap && `${props.$rowgap}px`};
	column-gap: ${(props) => props.$colgap && `${props.$colgap}px`};
	${(props) => (props.$rows ? `grid-template-rows: ${props.$rows};` : '')}
`;

const StyledLeftContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 45px;
`;

const StyledSectionHeading = styled(StyledFlexContainer).attrs({
	as: 'div',
	$direction: 'row',
	$gap: 10,
	$align: 'center',
})`
	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
		opacity: 0.75;
	}

	${TextLabel} {
		font-size: 16px;
		color: #ffffff;
		opacity: 0.75;
	}
`;

const StyledQuickAccessHeading = styled(StyledFlexContainer).attrs({
	as: 'div',
	$direction: 'row',
	$align: 'flex-start',
})`
	.line {
		width: 42px;
		height: 18px;
		background-image: url(${line});
		background-repeat: no-repeat;
		background-position: center;
		background-size: contain;
		margin-top: 7px;

		&:last-child {
			transform: scaleX(-1);
		}
	}

	.center {
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 0 9px;

		img {
			width: 14px;
			height: 14px;
			user-select: none;
			pointer-events: none;
			opacity: 0.25;
		}

		${TextLabel} {
			font-style: italic;
			font-weight: 400;
			font-size: 14px;
			color: #ffffff;
			opacity: 0.25;
		}
	}
`;

const StyledCountContainer = styled(StyledFlexContainer).attrs({
	as: 'div',
	$direction: 'row',
	$align: 'center',
	$gap: 10,
})`
	${TextLabel} {
		font-weight: 400;
		font-size: 14px;
		color: rgba(255, 255, 255, 0.5);

		span {
			font-size: 16px;
			color: #ffffff;
		}
	}

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
		opacity: 0.25;
	}
`;

const StyledRightContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 40px;
`;

const StyledDelZoneBgStripes = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	mask-image: url(${delZoneBg});
	mask-position: center;
	mask-repeat: no-repeat;
	-webkit-mask-image: url(${delZoneBg});
	-webkit-mask-position: center;
	-webkit-mask-repeat: no-repeat;
	background-color: rgba(255, 255, 255, 0.02);
	transition: background-color 0.3s ease;
`;

const StyledDelZoneContainer = styled(StyledFlexContainer).attrs({
	as: 'div',
	$align: 'center',
	$justify: 'center',
	$gap: 13,
})<{ $active?: boolean; $full?: boolean; $hover?: boolean }>`
	position: relative;
	width: 100%;
	height: ${(props) => (props.$full ? 600 : 170)}px;
	opacity: ${(props) => (props.$active ? 1 : 0.5)};
	background: rgba(255, 255, 255, 0.02);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	overflow: hidden;
	transition:
		opacity 0.3s ease,
		background 0.3s ease,
		border 0.3s ease;

	${(props) =>
		props.$active &&
		props.$hover &&
		css`
			background: rgba(199, 30, 30, 0.25);
			border: 1px solid #c71e1e;

			${StyledDelZoneBgStripes} {
				background-color: rgba(199, 30, 30, 0.1);
			}
		`}
`;

const StyledSelectButton = styled(StyledFlexContainer).attrs({
	as: 'div',
	$align: 'center',
	$justify: 'center',
})<{ $active?: boolean }>`
	position: relative;
	width: 20px;
	height: 20px;
	border-radius: 6px;
	background-color: rgba(255, 255, 255, 0.1);
	transition: background-color 0.3s ease;
	cursor: pointer;

	&:hover {
		background-color: #ffffff;

		.arrow {
			filter: invert(100%);
		}
	}

	.arrow {
		width: 10px;
		height: 5px;
		background-image: url(${arrowDown});
		background-repeat: no-repeat;
		background-position: center;
		background-size: contain;
		rotate: ${(props) => (props.$active ? 180 : 0)}deg;
		transition:
			rotate 0.3s ease,
			filter 0.3s ease;
	}
`;

const StyledDropdownContainer = styled.div`
	position: absolute;
	top: 30px;
	right: 0;
	width: 240px;
	z-index: 10;

	opacity: 0;
	transition: opacity 0.3s ease;

	&.results-enter {
		opacity: 0;
	}

	&.results-enter-active,
	&.results-enter-done,
	&.results-exit {
		opacity: 1;
	}

	&.results-exit-active {
		opacity: 0;
	}
`;

export {
	GRID_STYLES,
	StyledBannerContainer,
	StyledContainer,
	StyledCountContainer,
	StyledDelZoneBgStripes,
	StyledDelZoneContainer,
	StyledDropdownContainer,
	StyledFlexContainer,
	StyledGridContainer,
	StyledInventoryContainer,
	StyledLeftContainer,
	StyledQuickAccessHeading,
	StyledRightContainer,
	StyledSectionHeading,
	StyledSelectButton,
};
